import React from 'react';
import userServices from '../utils/user';
import {
  Avatar,
  AppBar,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { adminRoutes } from './config/routes';
import { AppRoute } from '../types';
import { useNavigate, Outlet } from 'react-router-dom';
import { useModal } from 'mui-modal-provider';
import { isAuthenticated } from '../utils/legacyauth';

const pages: AppRoute[] = adminRoutes.filter((route) => route.primary === true);
const settings: AppRoute[] = adminRoutes.filter(
  (route) => route.primary === false
);

export const Admin = (): JSX.Element => {
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUserXs, setAnchorElUserXs] =
    React.useState<null | HTMLElement>(null);
  const { showModal } = useModal();
  const navigate = useNavigate();
  const username: string = userServices.getName();

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleOpenUserMenuXs = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUserXs(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleCloseUserMenuXs = () => {
    setAnchorElUserXs(null);
  };

  const handleLogout = () => {
    userServices.logOut();
    navigate('/login');
  };

  if (!isAuthenticated()) {
    return <></>;
  }

  return isAuthenticated() ? (
    <>
      <AppBar position="fixed">
        <Container maxWidth={false} sx={{ pl: 0, backgroundColor: 'red' }}>
          <Toolbar disableGutters={true}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}
            >
              <img src="/images/logo.png" alt="logo" />
            </Typography>

            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: { xs: 'row' },
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  width: '50px',
                  display: { xs: 'flex', md: 'none' },
                }}
              >
                <img
                  style={{
                    padding: '10px',
                    objectFit: 'cover',
                    height: '35px',
                    width: '65px',
                  }}
                  src="/images/logo_compact_white.png"
                  alt="logo"
                />
              </Box>

              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  flexDirection: 'row',
                }}
              >
                <Box>
                  <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleOpenNavMenu}
                    color="inherit"
                  >
                    <MenuIcon />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorElNav}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorElNav)}
                    onClose={handleCloseNavMenu}
                    disableScrollLock={true}
                  >
                    {pages.map((page: AppRoute) => (
                      <Link
                        key={page.title}
                        href={page.path}
                        sx={{
                          textDecoration: 'none',
                        }}
                      >
                        <MenuItem>
                          <Typography
                            textAlign="center"
                            sx={{
                              fontSize: 12,
                            }}
                          >
                            {page.title}
                          </Typography>
                        </MenuItem>
                      </Link>
                    ))}
                  </Menu>
                </Box>

                <Box>
                  <Tooltip title="Open settings">
                    <IconButton onClick={handleOpenUserMenuXs}>
                      <Avatar alt={username} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    id="menu-appbar-user-xs"
                    anchorEl={anchorElUserXs}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorElUserXs)}
                    onClose={handleCloseUserMenuXs}
                    disableScrollLock={true}
                  >
                    {settings.map((setting) => (
                      <Link
                        key={setting.title}
                        href={setting.path}
                        sx={{
                          textDecoration: 'none',
                        }}
                      >
                        <MenuItem>
                          <Typography textAlign="center" sx={{ fontSize: 12 }}>
                            {setting.title}
                          </Typography>
                        </MenuItem>
                      </Link>
                    ))}
                    <MenuItem onClick={handleLogout}>
                      <Typography textAlign="center" sx={{ fontSize: 12 }}>
                        Logout
                      </Typography>
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                justifyContent: 'flex-end',
                display: { xs: 'none', md: 'flex' },
              }}
            >
              {pages.map((page: AppRoute) => (
                <Button
                  key={page.title}
                  href={page.path ? page.path : undefined}
                  sx={{ my: 2, color: 'white', display: 'block', fontSize: 18 }}
                >
                  <Typography sx={{ fontSize: 14 }}> {page.title} </Typography>
                </Button>
              ))}
            </Box>

            <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' } }}>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu}>
                  <Avatar alt={username} />
                </IconButton>
              </Tooltip>
              <Menu
                sx={{
                  mt: 0.5,
                  position: 'fixed',
                }}
                id="menu-appbar-user"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {settings.map((setting) => (
                  <Link
                    key={setting.title}
                    href={setting.path}
                    sx={{
                      textDecoration: 'none',
                    }}
                  >
                    <MenuItem>
                      <Typography textAlign="center" sx={{ fontSize: 12 }}>
                        {setting.title}
                      </Typography>
                    </MenuItem>
                  </Link>
                ))}
                <MenuItem onClick={handleLogout}>
                  <Typography textAlign="center" sx={{ fontSize: 12 }}>
                    Logout
                  </Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <Grid sx={{ pt: 10, px: 2 }}>
        <Outlet />
      </Grid>
    </>
  ) : (
    <></>
  );
};
